import { roundingDividers } from '@teinor/erp/prices'
import { decimalsDict, TwinParameters } from '@teinor/erp/types/company/config/parameter'
import { ComponentWithPermissions } from '../../../../../../../baseComponents/CreateRoutering/types'
import { CustomSelectDropDownSortedNumbers } from '../../../../../../../forms/CustomSelect/DropDown'
import { FormRender } from '../../../../../../../forms/FormRenderer/types'
import { valueOrDefaultValue } from '../../../../../../../utils/globals/valueOrDefaultValue'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { TextForReference, TextForSubproducts } from './Subcomponents'
import {type_code_bar} from '@teinor/erp/types/company/product'
import { addDictionaryExtraLine, dictionaryComplexFromArr, dictionaryComplexFromSimpleDict } from '../../../../../../../utils/globals/dictionary'
import SingleFormLayout from '../../../../../../../baseComponents/Layout/SingleFormLayout'
import useProductAddonTypeDict from '../../../../../../../utils/hooks/orders/useProductAddonTypeDict'
import ConfigProductCodebars from '../../../ConfigProductCodebars'
import { returnClassOfSpanTemplate } from '../../../../../../../utils/globals/tailwind'
import { useCallback } from 'react'
import { changeErrorMessage } from '../../../../../../../utils/reducers/reduxDispatch'
import { addErrorWhenBlur, removeClassListError } from '../../../../../../../forms/Input/functions'

interface ProductParametersProps extends ComponentWithPermissions {
    basicData: TwinParameters
    getData: () => Promise<void>
}

interface TwinParametersCustom extends TwinParameters {
    textForRef: string
}

const ProductParameters: React.FC<ProductParametersProps> = ({ basicData, getData, userPermissions }) => {
    const { t } = useTwinTranslation()
    const roundingDict = dictionaryComplexFromSimpleDict(roundingDividers as any)
    const dictionaryTypesCodeBar = dictionaryComplexFromArr(type_code_bar as any)
    const decimalsDictParsed = dictionaryComplexFromSimpleDict(decimalsDict as any)
    const productAddonTypeDict = useProductAddonTypeDict()
    const { checkDictionaryPrices } = useProductParametersLogic()
    const onOff = {
        'on': { name: t('on', 'Activado') },
        'off': { name: t('disabledS', 'Desactivado') },
    }
    const typeDisplayProducts = {
        'onlyParents': { name: t('onlyParents', 'Solo padres') },
        'onlyVariants': { name: t('onlyVariants', 'Solo variantes') },
        'allProducts': { name: t('allProducts', 'Todos los productos') },
    }
    const fields: FormRender<TwinParametersCustom> = [
        {
            cols: 3,
            title: t('priceParameters', 'Parámetros de precios'),
            elements: [
                {
                    name: 'formulaCalculation',
                    label: t('formulaCalculationParam', 'Cuando se calcula la fórmula'),
                    component: 'CustomSelect',
                    items: {
                        'before': { name: t('formulaCalcParamBefore', 'Antes del redondeo')},
                        'after': { name: t('formulaCalcParamAfter', 'Despues del redondeo')},
                    }
                },
                {
                    name: 'roundingPriceType',
                    label: t('roundingPriceTypeParam', 'Tipo de redondeo'),
                    component: 'CustomSelect',
                    items: {
                        'lower': { name: t('standard', 'Standard')},
                        'higher': { name: t('paramHigher', 'A la alza')},
                    }
                },
                {
                    name: 'subProductPrices',
                    label: t('subProductPricesParam', 'Precios de los subproductos'),
                    component: 'CustomSelect',
                    items: {
                        'heredate': { name: t('fromParent', 'Del padre')},
                        'self': { name: t('selfPrices', 'Precios propios')},
                        'increment': { name: t('incrementFromParent', 'Incrementar sobre el padre')}
                    }
                },
                {
                    name: 'composedProductPrice',
                    label: t('composedProductPrice', 'Precio del producto compuesto'),
                    component: 'CustomSelect',
                    items: {
                        'self': { name: t('selfPrices', 'Precios propios') },
                        'heredate': { name: t('sumAllPriceChildren', 'Sumar precios de hijos') }
                    }
                },
                {
                    name: 'decimals',
                    label: t('decimals', 'Decimales'),
                    component: 'CustomSelect',
                    items: decimalsDictParsed
                },
                {
                    name: 'rounding',
                    label: t('rounding', 'Redondeo'),
                    component: 'CustomSelect',
                    items: addDictionaryExtraLine(roundingDict),
                    DropDownComponent: CustomSelectDropDownSortedNumbers,
                    sortItems: true
                },
                {
                    name: 'typeMargin',
                    label: t('typeMargin', 'Tipo de Margen'),
                    component: 'CustomSelect',
                    items: {
                        '0': { name: t('costMargin', 'Margen sobre el coste')},
                        '1': { name: t('costSale', 'Margen sobre la venta')},
                    }
                },
                {
                    name: 'limitMargin',
                    component: 'InputWithLabelMargin',
                    label: t('limitMargin', 'Margen límite'),
                    className: 'mt-auto'
                },
                {
                    name: 'calculateProductMarginType',
                    label: t('calculateProductMarginType', 'Tipo cálculo de Margen'),
                    component: 'CustomSelect',
                    items: {
                        '0': { name: t('perLine', 'Por línea')},
                        '1': { name: t('withAddons', 'Con complementos')},
                    }
                },
                {
                    name: 'dictionaryPrices',
                    component: 'InputWithLabelMargin',
                    label: t('dictionaryPricesComplex', 'Dictionario precios (0-9)'),
                    onBlur: checkDictionaryPrices,
                    maxLength: 10,
                    className: 'mt-auto'
                },
            ]
        },
        {
            cols: 3,
            title: t('referenceAutoParameters', 'Parámetros de referencias automáticas'),
            elements: [
                {
                    name: 'textForRef',
                    component: 'Custom',
                    render: TextForReference
                },
                {
                    name: 'typeCategoryReferenceGeneration',
                    label: t('typeReferenceCategory', 'Tipo de referencia categoría'),
                    component: 'CustomSelect',
                    items: {
                        'self': { name: t('ownCategory', 'De la propia categoría')},
                        'heredate': { name: t('heredateCategoryParam', 'Entre todas las categorías padre y propia')},
                    }
                },
                {
                    name: 'referenceGeneration',
                    component: 'InputWithLabelMargin',
                    label: t('productParentRef', 'Referencia de los productos padre'),
                    className: 'mt-auto'
                },
                {
                    name: 'childrenReferenceGeneration',
                    component: 'InputWithLabelMargin',
                    label: t('productChildrenRef', 'Referencia de los productos hijo'),
                    className: 'mt-auto'
                }
            ]
        },
        {
            cols: 3,
            title: t('subProductGenerationParameters', 'Parámetros de nombres automáticos de subproductos'),
            elements: [
                {
                    name: 'textForRef',
                    component: 'Custom',
                    render: TextForSubproducts
                },
                {
                    name: 'subProductNameGen',
                    component: 'InputWithLabelMargin',
                    label: t('nameGeneration', 'Generación de los nombres'),
                    className: 'mt-auto'
                },
                {
                    name: 'subProductNameVariantSeparator',
                    component: 'InputWithLabelMargin',
                    label: t('variantSeparatorInName', 'Separador entre variantes en el nombre'),
                    className: 'mt-auto'
                }
            ]
        },
        {
            cols: 3,
            title: t('stockParameters', 'Parámetros de stock'),
            elements: [
                {
                    name: 'operateStockProducts',
                    label: t('operateStockInProducts', 'Trabajar con stock en productos'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'operateStockServices',
                    label: t('operateStockInServices', 'Trabajar con stock en servicios'),
                    component: 'CustomSelect',
                    items: onOff
                },
            ]
        },
        {
            cols: 3,
            title: t('otherParameters', 'Otros parametros'),
            elements: [
                {
                    name: 'type_code_bar',
                    label: t('defaultTypeCodeBar', 'Tipo de código de barras por defecto'),
                    component: 'CustomSelect',
                    items: dictionaryTypesCodeBar
                },
                {
                    name: 'lotUnique',
                    label: t('lotUniqueNumberMustBeUnique', 'El número de lote es único'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'productAddonType',
                    label: t('productAddonType', 'Tipo de complementos'),
                    component: 'CustomSelect',
                    items: productAddonTypeDict
                },
                {
                    name: 'typeDisplayProducts',
                    label: t('listProductsType', 'Manera de listar productos'),
                    component: 'CustomSelect',
                    items: typeDisplayProducts
                },
                {
                    name: 'textForRef',
                    component: 'Custom',
                    render: ({ extraProps }) => <ConfigProductCodebars getData={extraProps.getData} className={returnClassOfSpanTemplate(2)} />,
                    extraProps: {
                        getData: getData
                    }
                },
            ]
        },
    ]
    const parsedFields = valueOrDefaultValue(fields, userPermissions, basicData)
    return (
        <SingleFormLayout action='/api/app/config/parameter/updateParameters' onSubmit={getData} sections={parsedFields} userPermissions={userPermissions} />
    )
}


const useProductParametersLogic = () => {

    const {t} = useTwinTranslation()
    const errorMessageDictionaryPrices = t('notMatchDictionary', 'Hay un error en el diccionario de precios')
    const checkDictionaryPrices: React.FocusEventHandler<HTMLInputElement> = useCallback((e) => {
        const value = e.currentTarget.value
        const checked = /^[a-zA-Z]{10}$/.test(value) && new Set(value).size === 10
        if(!checked){
            changeErrorMessage(errorMessageDictionaryPrices)
            addErrorWhenBlur(e.currentTarget, e.currentTarget.parentElement, false)
        }else {
            removeClassListError(e.currentTarget, e.currentTarget.parentElement, true)
        }
    }, [errorMessageDictionaryPrices])

    return { checkDictionaryPrices }
}

export default ProductParameters